import React from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calculator, Car, TrendingUp } from "lucide-react";
import { COMPANY_LIST } from "../../companies";
import { seniorityLabel, fmtDate, contractTerms } from "./adminUtils";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]";
const labelCls = "block text-[12px] font-semibold text-gray-600 mb-1";
const round1 = (n) => Math.round(n * 10) / 10;

export const EmployeeFormDialog = ({ form, setForm, depts, ccnls = [], benefits = [], saving, onSave, onClose }) => {
  const selectedCcnl = form ? ccnls.find((c) => c.id === form.ccnl_id) : null;
  const pct = Number(form?.part_time_pct);
  const validPct = Number.isFinite(pct) && pct > 0 ? pct : 100;
  const propFerie = selectedCcnl ? round1(selectedCcnl.annual_ferie_days * validPct / 100) : null;
  const propRol = selectedCcnl ? round1(selectedCcnl.annual_rol_hours * validPct / 100) : null;
  const propExf = selectedCcnl ? round1((selectedCcnl.exfestivita_hours || 0) * validPct / 100) : null;
  const applyProportion = () => setForm({ ...form, ferie_balance: propFerie, rol_balance: propRol, exfestivita_balance: propExf });
  const terms = selectedCcnl ? contractTerms(form, selectedCcnl) : null;
  const handleSave = () => {
    if (!form.hire_date) {
      toast.error(form.is_external ? "Inserisci la data di inizio contratto" : "Inserisci la data di assunzione");
      return;
    }
    onSave();
  };
  // Auto assegnate a questo dipendente (calcolato una volta, riusato sotto).
  const assignedCars = form?._edit ? benefits.filter((b) => b.car_employee_id === form.id && (b.car_model || b.car_plate)) : [];

  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form?._edit ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle>
          <DialogDescription className="sr-only">Modulo per inserire o modificare i dati di un dipendente.</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Nome completo *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Matricola *</label>
              <input value={form.matricola} disabled={form._edit} onChange={(e) => setForm({ ...form, matricola: e.target.value })} className={`${inputCls} disabled:bg-gray-100 disabled:text-gray-400`} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Email aziendale *</label>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{form._edit ? "Nuova password" : "Password *"}</label>
              <input type="text" value={form.password} placeholder={form._edit ? "(invariata)" : ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Ruolo</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dipendente">Dipendente</SelectItem>
                  <SelectItem value="responsabile">Responsabile</SelectItem>
                  <SelectItem value="admin">Amministratore HR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Società</label>
              <Select value={form.company || "teleborsa"} onValueChange={(v) => setForm({ ...form, company: v })}>
                <SelectTrigger data-testid="emp-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPANY_LIST.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Sede di lavoro</label>
              <Select value={form.sede || "Roma"} onValueChange={(v) => setForm({ ...form, sede: v })}>
                <SelectTrigger data-testid="emp-sede"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Roma">Roma (patrono 29/6 SS. Pietro e Paolo)</SelectItem>
                  <SelectItem value="Milano">Milano (patrono 7/12 Sant'Ambrogio)</SelectItem>
                  <SelectItem value="Ferrara">Ferrara (patrono 23/4 San Giorgio)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Reparto</label>
              <Select value={form.department_id || "none"} onValueChange={(v) => setForm({ ...form, department_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Qualifica</label>
              <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className={inputCls} />
            </div>

            <div>
              <label className={labelCls}>{form.is_external ? "Data inizio contratto *" : "Data di assunzione *"}</label>
              <input type="date" value={form.hire_date || ""} data-testid="emp-hire-date" onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Anzianità</label>
              <div data-testid="emp-seniority" className="w-full border border-gray-200 bg-gray-50 rounded-sm px-3 py-2 text-[14px] text-[#16345c] font-semibold">
                {form.hire_date ? seniorityLabel(form.hire_date) : "—"}
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">In mesi, alla data odierna (matura dopo il 15° giorno).</p>
            </div>
            <div>
              <label className={labelCls}>Fine contratto</label>
              <input type="date" value={form.contract_end || ""} data-testid="emp-contract-end" onChange={(e) => setForm({ ...form, contract_end: e.target.value })} className={inputCls} />
              <p className="text-[11px] text-gray-400 mt-0.5">Vuoto = tempo indeterminato. Se valorizzata, determina l'anzianità di servizio alla cessazione e quindi i giorni di preavviso.</p>
            </div>

            <div className="sm:col-span-2">
              <label className={labelCls}>CCNL applicato</label>
              <Select value={form.ccnl_id || "none"} onValueChange={(v) => setForm({ ...form, ccnl_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="emp-ccnl"><SelectValue placeholder="Nessun CCNL" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {ccnls.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {ccnls.length === 0 && <p className="text-[11px] text-amber-600 mt-0.5">Nessun CCNL definito. Aggiungili dalla scheda "CCNL" in Gestione HR.</p>}
            </div>

            {selectedCcnl && (selectedCcnl.level_terms?.length > 0) ? (
              <>
                <div>
                  <label className={labelCls}>Livello / inquadramento</label>
                  <Select value={form.level || "none"} onValueChange={(v) => setForm({ ...form, level: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="emp-level"><SelectValue placeholder="Nessun livello" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      {selectedCcnl.level_terms.map((t) => <SelectItem key={t.level} value={t.level}>{t.level}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className={labelCls}>Termini contrattuali (auto)</label>
                  <div data-testid="emp-contract-terms" className="w-full border border-slate-200 bg-slate-50 rounded-sm px-3 py-2 text-[12.5px] text-gray-700 space-y-0.5">
                    <div>Anzianità: <b className="text-[#16345c]">{terms?.bracket || "—"}</b></div>
                    <div>Periodo di prova: <b className="text-[#16345c]">{form.level && terms?.probation_days ? `${terms.probation_days} giorni` : "—"}</b></div>
                    <div>Preavviso: <b className="text-[#16345c]">{form.level && terms?.notice_days ? `${terms.notice_days} giorni` : "—"}</b></div>
                  </div>
                  {!form.level && <p className="text-[11px] text-amber-600 mt-0.5">Seleziona un livello per calcolare prova e preavviso.</p>}
                </div>
              </>
            ) : selectedCcnl ? (
              <div className="sm:col-span-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-[12px] text-amber-800" data-testid="emp-level-missing">
                Questo CCNL non ha livelli configurati, quindi non è possibile scegliere l'inquadramento. Aggiungi i livelli (con periodo di prova e preavviso) dalla scheda <b>"CCNL"</b> in Gestione HR.
              </div>
            ) : null}

            {form._edit && assignedCars.length > 0 && (
              <div className="sm:col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="emp-assigned-cars">
                <div className="flex items-center gap-2 text-[12.5px] font-bold text-[#16345c] mb-1.5"><Car size={15} className="text-[#0d6cb8]" /> Auto aziendale assegnata</div>
                {assignedCars.map((b) => (
                  <div key={b.id} className="text-[12.5px] text-gray-700 flex items-center gap-2 flex-wrap">
                    {b.car_model && <b className="text-[#16345c]">{b.car_model}</b>}
                    {b.car_plate && <span className="font-mono uppercase tracking-wide bg-white border border-gray-200 px-1.5 py-0.5 rounded-sm">{b.car_plate}</span>}
                    {b.car_insurance_expiry && <span className="text-gray-500">Assic.: {fmtDate(b.car_insurance_expiry)}</span>}
                    {b.car_revision_expiry && <span className="text-gray-500">Revis.: {fmtDate(b.car_revision_expiry)}</span>}
                  </div>
                ))}
              </div>
            )}

            {form._edit && (form.scatti_history || []).length > 0 && (
              <div className="sm:col-span-2 bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="emp-scatti-history">
                <div className="flex items-center gap-2 text-[12.5px] font-bold text-emerald-800 mb-1.5"><TrendingUp size={15} /> Storico scatti di anzianità ({form.scatti_history.length})</div>
                <div className="space-y-0.5">
                  {form.scatti_history.map((s) => (
                    <div key={s.numero} className="text-[12px] text-gray-700 flex items-center gap-2">
                      <span className="font-bold text-emerald-700">{s.numero}° scatto</span>
                      <span className="text-gray-500">maturato il {fmtDate(s.data)} · {s.mese_anzianita} mesi di anzianità</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>% Part-time</label>
              <div className="relative">
                <input type="number" min="1" max="100" step="1" value={form.part_time_pct ?? 100} data-testid="emp-part-time" onChange={(e) => setForm({ ...form, part_time_pct: e.target.value })} className={`${inputCls} pr-8`} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[13px]">%</span>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">100 = full-time.</p>
            </div>
            <div>
              <label className={labelCls}>Giorni ferie (saldo)</label>
              <input type="number" step="0.5" value={form.ferie_balance} data-testid="emp-ferie-balance" onChange={(e) => setForm({ ...form, ferie_balance: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Permessi ROL (ore, saldo)</label>
              <input type="number" step="0.5" value={form.rol_balance} data-testid="emp-rol-balance" onChange={(e) => setForm({ ...form, rol_balance: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Ex festività (ore, saldo)</label>
              <input type="number" step="0.5" value={form.exfestivita_balance ?? 0} data-testid="emp-exfestivita-balance" onChange={(e) => setForm({ ...form, exfestivita_balance: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Permesso studio (ore, saldo)</label>
              <input type="number" step="0.5" value={form.studio_balance ?? 150} data-testid="emp-studio-balance" onChange={(e) => setForm({ ...form, studio_balance: e.target.value })} className={inputCls} />
              <p className="text-[11px] text-gray-400 mt-0.5">Diritto allo studio: 150 ore annue.</p>
            </div>

            <div className="sm:col-span-2 border-t border-gray-100 pt-3">
              <div className="text-[12.5px] font-bold text-[#16345c] mb-1">Saldi periodo precedente (residui da riportare)</div>
              <p className="text-[11px] text-gray-400 mb-2">Residui dell'anno/periodo precedente. Usa il pulsante "Riporta saldi" nella scheda <b>Maturazioni</b> per sommarli una-tantum ai saldi correnti.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Ferie prec. (gg)</label>
                  <input type="number" step="0.5" value={form.prev_ferie_balance ?? 0} data-testid="emp-prev-ferie" onChange={(e) => setForm({ ...form, prev_ferie_balance: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>ROL prec. (ore)</label>
                  <input type="number" step="0.5" value={form.prev_rol_balance ?? 0} data-testid="emp-prev-rol" onChange={(e) => setForm({ ...form, prev_rol_balance: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Ex-fest. prec. (ore)</label>
                  <input type="number" step="0.5" value={form.prev_exfestivita_balance ?? 0} data-testid="emp-prev-exfestivita" onChange={(e) => setForm({ ...form, prev_exfestivita_balance: e.target.value })} className={inputCls} />
                </div>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className={labelCls}>Welfare assegnato a questo dipendente</label>
              <div className="border border-gray-200 rounded-sm p-2 max-h-[120px] overflow-y-auto space-y-1">
                {benefits.length === 0 ? <p className="text-[11px] text-gray-400">Nessun benefit disponibile.</p> :
                  benefits.map((b) => {
                    const sel = (form.custom_benefit_ids || []).includes(b.id);
                    return (
                      <label key={b.id} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                        <input type="checkbox" checked={sel} data-testid={`emp-benefit-${b.id}`} onChange={(e) => {
                          const cur = form.custom_benefit_ids || [];
                          setForm({ ...form, custom_benefit_ids: e.target.checked ? [...cur, b.id] : cur.filter((x) => x !== b.id) });
                        }} className="accent-[#16345c]" />
                        {b.title}
                      </label>
                    );
                  })}
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">Assegna benefit specifici oltre a quelli previsti per la categoria.</p>
            </div>

            {selectedCcnl && (
              <div className="sm:col-span-2 bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="reproportion-box">
                <div className="flex items-start gap-2">
                  <Calculator size={16} className="text-[#0d6cb8] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[12.5px] font-bold text-[#16345c]">Riproporzionamento part-time ({validPct}%)</div>
                    <div className="text-[12.5px] text-gray-600 mt-0.5">
                      Spettanza annua: <b className="text-[#16345c]">{propFerie} gg</b> ferie · <b className="text-[#16345c]">{propRol} ore</b> ROL · <b className="text-[#16345c]">{propExf} ore</b> ex-festività
                      <span className="text-gray-400"> (FT: {selectedCcnl.annual_ferie_days} gg / {selectedCcnl.annual_rol_hours} ore / {selectedCcnl.exfestivita_hours || 0} ore)</span>
                    </div>
                  </div>
                  <button type="button" onClick={applyProportion} data-testid="apply-proportion-btn" className="shrink-0 text-[12px] font-bold text-white bg-[#0d6cb8] hover:bg-[#0a5590] px-3 py-1.5 rounded-sm transition-colors">
                    Applica al saldo
                  </button>
                </div>
              </div>
            )}

            <div className="sm:col-span-2">
              <label className={`flex items-start gap-3 border rounded-sm px-3 py-2.5 cursor-pointer transition-colors ${form.is_external ? "border-orange-400 bg-orange-50" : "border-gray-300"}`}>
                <input type="checkbox" checked={!!form.is_external} data-testid="emp-external" onChange={(e) => setForm({ ...form, is_external: e.target.checked })} className="mt-0.5 accent-[#16345c] w-4 h-4" />
                <span>
                  <span className="block text-[13px] font-bold text-[#16345c]">Collaboratore esterno</span>
                  <span className="block text-[11.5px] text-gray-500 leading-snug">Esclude ferie, permessi, malattia, trasferte, presenze, buste paga e le altre funzioni tipiche del dipendente. Restano solo i permessi da Responsabile o Admin HR, se assegnati.</span>
                </span>
              </label>
            </div>
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-60">{saving ? "Salvataggio..." : "Salva"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
